import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Flame,
  HelpCircle,
  Moon,
  RefreshCw,
  Repeat2,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserPlus,
  UserX,
  Users,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const STAGES = [
  { key: "new", label: "Nuevos", icon: UserPlus, hint: "Cuenta reciente, todavía sin activación sostenida." },
  { key: "activated", label: "Activados", icon: CheckCircle2, hint: "Ya usaron KD9 recientemente." },
  { key: "recurring", label: "Recurrentes", icon: Repeat2, hint: "Regresan de forma repetida." },
  { key: "highly_active", label: "Muy activos", icon: Flame, hint: "Uso intenso y reciente." },
  { key: "dormant", label: "Dormidos", icon: Moon, hint: "Entre 8 y 30 días sin actividad." },
  { key: "inactive", label: "Inactivos", icon: UserX, hint: "Más de 30 días sin actividad." },
];

const ROLE_LABELS = {
  user: "Usuario",
  student: "Estudiante",
  consultant: "Consultor",
  creator: "Creador",
  support: "Soporte",
  admin: "Administrador",
  director: "Director",
};

const fmtDate = (value) => {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Bogota",
  }).format(date);
};

const fmtDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return total ? `${total}s` : "0 min";
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

const roleLabel = (row) => {
  const order = ["director", "admin", "support", "consultant", "creator", "student", "user"];
  const role = order.find((item) => (row?.roles || []).includes(item)) || row?.role_base || "user";
  return ROLE_LABELS[role] || role;
};

const StageBadge = ({ stage }) => {
  const meta = STAGES.find((item) => item.key === stage) || STAGES[0];
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mont text-[10px] uppercase tracking-wider" style={{ borderColor: "var(--kd9-border)", background: "var(--kd9-bg-2)" }}>
      <Icon size={11} className="kd9-gold-text" />
      {meta.label}
    </span>
  );
};

const Trend = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>Sin comparación</span>;
  }
  const number = Number(value) || 0;
  const down = number < 0;
  const Icon = down ? TrendingDown : TrendingUp;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px]" style={{ color: down ? "#FCA5A5" : "#86EFAC" }}>
      <Icon size={12} />
      {number > 0 ? "+" : ""}{number}%
    </span>
  );
};

const RetentionRadar = () => {
  const [filters, setFilters] = useState({ search: "", role: "", stage: "", risk_only: false });
  const [data, setData] = useState({ summary: null, items: [], thresholds: null, methodology: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRules, setShowRules] = useState(false);

  const params = useMemo(() => {
    const result = { limit: 200 };
    if (filters.search) result.search = filters.search;
    if (filters.role) result.role = filters.role;
    if (filters.stage) result.stage = filters.stage;
    if (filters.risk_only) result.risk_only = true;
    return result;
  }, [filters]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/activity/retention-radar", { params });
      setData(response.data || { summary: null, items: [], total: 0 });
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = window.setTimeout(() => load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => load({ quiet: true }), 60_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const summary = data.summary || {};
  const stageMap = useMemo(
    () => Object.fromEntries((summary.stages || []).map((item) => [item.key, item])),
    [summary.stages]
  );

  return (
    <PageShell
      kicker="Dirección y Administración · Retención"
      title="RADAR DE RETENCIÓN KD9"
      desc="Clasificación explicable del ciclo de vida de usuarios para distinguir activación, recurrencia, alto uso y señales de abandono."
      testId="retention-radar-page"
      badge={<span className="inline-flex items-center gap-2 text-[10px] font-mont uppercase tracking-widest kd9-gold-text"><ShieldCheck size={14}/> Reglas transparentes</span>}
    >
      {error && (
        <div className="mb-5 rounded-xl border p-4 flex items-center gap-3" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,.4)" }} role="alert">
          <span className="font-mont text-xs flex-1">{error}</span>
          <button type="button" onClick={() => load()} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-1.5"><RefreshCw size={12}/> Reintentar</button>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5" data-testid="retention-summary">
        {[
          ["population", "Población analizada", Users],
          ["healthy_active", "Activos saludables", Activity],
          ["at_risk", "Señal de caída", AlertTriangle],
          ["dormant_or_inactive", "Dormidos + inactivos", Moon],
        ].map(([key, label, Icon]) => (
          <div key={key} className="kd9-card p-4" data-testid={`retention-metric-${key}`}>
            <Icon size={17} className="kd9-gold-text mb-3" />
            <div className="font-cinzel text-2xl">{loading && !data.summary ? "…" : Number(summary[key] || 0).toLocaleString("es-CO")}</div>
            <div className="font-mont text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
          </div>
        ))}
      </div>

      <section className="mb-6" aria-labelledby="retention-cycle-title">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 id="retention-cycle-title" className="font-cinzel text-sm kd9-gold-text tracking-[.14em]">CICLO DE VIDA KD9</h2>
            <p className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>Seleccioná una etapa para filtrar la población.</p>
          </div>
          <button type="button" onClick={() => setShowRules(true)} className="kd9-btn-outline rounded-lg px-3 py-2 text-[10px] inline-flex items-center gap-1.5">
            <HelpCircle size={13}/> Ver reglas
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6" data-testid="retention-stage-grid">
          {STAGES.map((stage) => {
            const Icon = stage.icon;
            const row = stageMap[stage.key] || { count: 0, percent: 0 };
            const selected = filters.stage === stage.key;
            return (
              <button
                type="button"
                key={stage.key}
                onClick={() => setFilters((current) => ({ ...current, stage: selected ? "" : stage.key, risk_only: false }))}
                className="kd9-card p-4 text-left transition-transform hover:-translate-y-0.5"
                style={{
                  outline: selected ? "1px solid var(--kd9-gold)" : "none",
                  background: selected ? "rgba(212,175,55,.07)" : undefined,
                }}
                data-testid={`retention-stage-${stage.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Icon size={18} className="kd9-gold-text"/>
                  <span className="font-mono text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{row.percent || 0}%</span>
                </div>
                <div className="font-cinzel text-2xl mt-3">{Number(row.count || 0).toLocaleString("es-CO")}</div>
                <div className="font-mont text-[10px] uppercase tracking-widest">{stage.label}</div>
                <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: "var(--kd9-border)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, Number(row.percent || 0))}%`, background: "var(--kd9-gold)" }} />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <div className="kd9-card p-4 mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5" data-testid="retention-filters">
        <div className="relative xl:col-span-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--kd9-text-soft)" }} />
          <input
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Buscar nombre, correo o ID KD9"
            className="w-full rounded-xl border bg-transparent py-2.5 pl-9 pr-3 font-mont text-xs outline-none"
            style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)" }}
          />
        </div>
        <select
          value={filters.role}
          onChange={(event) => setFilters((current) => ({ ...current, role: event.target.value }))}
          className="rounded-xl border px-3 py-2.5 font-mont text-xs"
          style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)", background: "var(--kd9-bg)" }}
          aria-label="Filtrar por rol"
        >
          <option value="">Todos los roles</option>
          {Object.entries(ROLE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setFilters((current) => ({ ...current, risk_only: !current.risk_only, stage: "" }))}
          className="rounded-xl border px-3 py-2.5 font-mont text-xs inline-flex items-center justify-center gap-2"
          style={{
            borderColor: filters.risk_only ? "rgba(239,68,68,.55)" : "var(--kd9-border)",
            background: filters.risk_only ? "rgba(239,68,68,.08)" : "transparent",
            color: filters.risk_only ? "#FCA5A5" : "var(--kd9-text)",
          }}
        >
          <AlertTriangle size={14}/> Solo señal de caída
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => load()} className="kd9-btn-outline flex-1 px-3 py-2.5 rounded-xl text-[10px] inline-flex items-center justify-center gap-1.5"><RefreshCw size={12}/> Actualizar</button>
          {(filters.stage || filters.role || filters.search || filters.risk_only) && (
            <button type="button" onClick={() => setFilters({ search: "", role: "", stage: "", risk_only: false })} className="p-2.5 rounded-xl border" style={{ borderColor: "var(--kd9-border)" }} aria-label="Limpiar filtros"><X size={14}/></button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-cinzel text-sm kd9-gold-text">USUARIOS EN EL RADAR</div>
          <div className="font-mont text-[9px] mt-1" style={{ color: "var(--kd9-text-soft)" }}>
            {summary.generated_at ? `Actualizado ${fmtDate(summary.generated_at)}` : ""}
          </div>
        </div>
        <div className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{Number(data.total || 0).toLocaleString("es-CO")} resultados</div>
      </div>

      {loading ? (
        <div className="kd9-card p-10 text-center font-mont text-sm">Calculando radar de retención…</div>
      ) : data.items?.length ? (
        <>
          <div className="hidden xl:block overflow-x-auto kd9-card">
            <table className="w-full min-w-[1180px] text-left" data-testid="retention-users-table">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--kd9-border)" }}>
                  {["Usuario", "Rol", "Etapa", "Última actividad", "Activo 7d", "7d anteriores", "Tendencia", "Sesiones 30d", "Días activos 30d", "Señal"].map((label) => (
                    <th key={label} className="px-4 py-3 font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }}>
                    <td className="px-4 py-3 max-w-[250px]">
                      <div className="font-mont text-xs font-semibold truncate">{[row.name, row.surname].filter(Boolean).join(" ") || "Usuario KD9"}</div>
                      <div className="font-mont text-[10px] truncate" style={{ color: "var(--kd9-text-soft)" }}>{row.email}</div>
                      {row.kd9_id && <div className="font-mono text-[9px] kd9-gold-text mt-0.5">{row.kd9_id}</div>}
                    </td>
                    <td className="px-4 py-3 font-mont text-xs">{roleLabel(row)}</td>
                    <td className="px-4 py-3"><StageBadge stage={row.stage}/></td>
                    <td className="px-4 py-3 font-mont text-[10px]">
                      {fmtDate(row.last_activity_at)}
                      <div className="mt-0.5" style={{ color: "var(--kd9-text-soft)" }}>
                        {row.days_since_last_activity === null ? "Sin actividad" : `${row.days_since_last_activity} días`}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDuration(row.active_7_seconds)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDuration(row.active_prev_7_seconds)}</td>
                    <td className="px-4 py-3"><Trend value={row.trend_7_vs_prev_7_percent}/></td>
                    <td className="px-4 py-3 font-mono text-xs">{Number(row.sessions_30 || 0)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{Number(row.active_days_30 || 0)}</td>
                    <td className="px-4 py-3">
                      {row.at_risk ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mont" style={{ color: "#FCA5A5" }}><AlertTriangle size={12}/> Caída reciente</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mont" style={{ color: "#86EFAC" }}><Sparkles size={12}/> Estable</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 xl:hidden" data-testid="retention-users-mobile">
            {data.items.map((row) => (
              <article key={row.id} className="kd9-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mont text-sm font-semibold truncate">{[row.name, row.surname].filter(Boolean).join(" ") || "Usuario KD9"}</div>
                    <div className="font-mont text-[10px] truncate" style={{ color: "var(--kd9-text-soft)" }}>{row.email}</div>
                  </div>
                  <StageBadge stage={row.stage}/>
                </div>
                <p className="font-cormorant text-base mt-3" style={{ color: "var(--kd9-text-soft)" }}>{row.stage_reason}</p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Activo 7d</span><span className="font-mono text-xs">{fmtDuration(row.active_7_seconds)}</span></div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>7d anteriores</span><span className="font-mono text-xs">{fmtDuration(row.active_prev_7_seconds)}</span></div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Sesiones 30d</span><span className="font-mono text-xs">{row.sessions_30}</span></div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Días activos 30d</span><span className="font-mono text-xs">{row.active_days_30}</span></div>
                </div>
                <div className="mt-4 pt-3 border-t flex items-center justify-between gap-3" style={{ borderColor: "var(--kd9-border)" }}>
                  <span className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{fmtDate(row.last_activity_at)}</span>
                  {row.at_risk ? <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: "#FCA5A5" }}><AlertTriangle size={12}/> Caída reciente</span> : <Trend value={row.trend_7_vs_prev_7_percent}/>}
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <div className="kd9-card p-10 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>No hay usuarios que coincidan con el filtro actual.</div>
      )}

      {showRules && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={(event) => { if (event.target === event.currentTarget) setShowRules(false); }}>
          <div className="kd9-card w-full max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="retention-rules-modal">
            <div className="p-5 border-b flex items-start gap-3" style={{ borderColor: "var(--kd9-border)" }}>
              <ShieldCheck size={20} className="kd9-gold-text mt-0.5"/>
              <div className="flex-1">
                <div className="font-cinzel">REGLAS DEL RADAR</div>
                <div className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>Sin IA opaca: cada etapa sale de reglas observables y auditables.</div>
              </div>
              <button type="button" onClick={() => setShowRules(false)} className="p-2 rounded-lg" aria-label="Cerrar reglas"><X size={16}/></button>
            </div>
            <div className="p-5 grid gap-4">
              {STAGES.map((stage) => {
                const Icon = stage.icon;
                return (
                  <div key={stage.key} className="rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)" }}>
                    <div className="flex items-center gap-2"><Icon size={16} className="kd9-gold-text"/><span className="font-mont text-xs font-semibold uppercase tracking-wider">{stage.label}</span></div>
                    <p className="font-cormorant text-base mt-2" style={{ color: "var(--kd9-text-soft)" }}>{stage.hint}</p>
                  </div>
                );
              })}
              {data.thresholds && (
                <div className="rounded-xl border p-4" style={{ borderColor: "rgba(212,175,55,.3)", background: "rgba(212,175,55,.05)" }}>
                  <div className="font-mont text-[10px] uppercase tracking-widest kd9-gold-text mb-3">Umbrales activos</div>
                  <div className="grid sm:grid-cols-2 gap-2 font-mont text-xs" style={{ color: "var(--kd9-text-soft)" }}>
                    <span>Nuevo: ≤ {data.thresholds.new_account_days} días</span>
                    <span>Dormido: &gt; {data.thresholds.dormant_after_days} días</span>
                    <span>Inactivo: &gt; {data.thresholds.inactive_after_days} días</span>
                    <span>Activación mínima: {fmtDuration(data.thresholds.activation_min_active_seconds)}</span>
                    <span>Recurrente: ≥ {data.thresholds.recurring_min_sessions_30} sesiones o {data.thresholds.recurring_min_active_days_30} días activos</span>
                    <span>Muy activo: ≥ {data.thresholds.highly_active_min_sessions_30} sesiones, {data.thresholds.highly_active_min_active_days_30} días o {fmtDuration(data.thresholds.highly_active_min_seconds_30)}</span>
                    <span className="sm:col-span-2">Señal de caída: actividad de los últimos 7 días ≤ {Math.round(Number(data.thresholds.at_risk_drop_ratio || 0) * 100)}% de los 7 días anteriores, con base previa mínima de {fmtDuration(data.thresholds.at_risk_baseline_seconds)}.</span>
                  </div>
                </div>
              )}
              <div className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
                <Clock3 size={14} className="inline mr-1 kd9-gold-text"/>
                Las fechas se muestran en hora Colombia. El sistema solo utiliza telemetría de sesión y actividad coarse; no registra teclas, contenido escrito ni decisiones privadas del usuario.
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default RetentionRadar;
